import { formatUsdBounds as revenueRange } from '@/lib/format-compact-usd';
// Company search filter builders for Crust Data CompanyDB API
// Uses Crust Data native format: filter_type key, operators =>/=</>/</(.)
// Reference: docs/plans/crustdata-company-search-api.md

import type { StartupProgramFilter } from '@/containers/search/startup-programs/startup-programs.types';
import { hasStartupPrograms } from '@/containers/search/startup-programs/startup-programs.utils';
import { normalizeDomain, normalizeDomainList } from '@/lib/normalize-domain';
import type { ActiveFilter } from '@/components/search/search-shell.component';
import type { CrmFilterSections } from '@/types/search-filters';
import { normalizeCalendarDate } from '@/utils/calendar-date.util';
import { withOrderedRanges } from '@/utils/search-filter-input.util';
import {
  buildCrmFilterChips,
  type CrmFilterChipContext,
} from '@/utils/crm-filter-chips.util';
import { FilterCompileError } from '@/utils/filter-compile-error.util';
import { expandCountrySelection } from '@/constants/country-groups';
import { expandIndustrySelection } from '@/constants/industry-groups';
import { naicsCodeRange, naicsLabel } from '@/constants/naics';
import { BRAND } from '@/lib/brand';

import { normalizeCompanyLinkedInUrls } from '@/utils/people-search.utils';

type FilterCondition = Record<string, unknown>;

export interface CompanySearchFilters {
  startupPrograms?: StartupProgramFilter;
  companyName: string;
  /** How ``companyName`` matches. Mirrors ``jobTitleMatchType`` on the
   *  people side: contains → ``[.]``, exact → ``=``, similar → ``(.)``. */
  companyNameMatchType: 'contains' | 'exact' | 'similar';
  domain: string;
  industriesInclude: string[];
  /** NAICS 2022 codes matched against the company's primary NAICS code. A
   *  shorter code (5412, 54) matches every six-digit code under it. */
  naicsCodes: string[];
  headcountRanges: string[];
  revenueMin: string;
  revenueMax: string;
  yearFoundedMin: string;
  yearFoundedMax: string;
  companyType: string[];
  fundingRoundTypes: string[];
  fundingAmountMin: string;
  growthMin: string;
  growthMax: string;
  // ── Expanded Crustdata CompanyDB filters (new in PRD 2026-05) ──
  // Excludes for industry, headcount, type.
  industriesExclude: string[];
  headcountRangesExclude: string[];
  companyTypeExclude: string[];
  // HQ country (ISO-3 alpha codes) include + exclude. Replaces the
  // older free-text ``hqLocations`` field that no sidebar surface
  // bound to (dead, removed 2026-05-24).
  hqCountriesIso3: string[];
  hqCountriesIso3Exclude: string[];
  // Largest-headcount-country picker — "Most employees in".
  largestHeadcountCountriesIso3: string[];
  // HQ state / city scoped autocompletes — same scoped pattern as the
  // People tab (Contact Location). Routed through the BE as
  // ``hq_location`` contains/not_contains filters since Crustdata
  // doesn't expose dedicated state/city fields on CompanyDB.
  hqStates: string[];
  hqStatesExclude: string[];
  hqCities: string[];
  hqCitiesExclude: string[];
  // LinkedIn categories (sibling of linkedin_industries — additional tags).
  linkedinCategoriesInclude: string[];
  crunchbaseCategoriesInclude: string[];
  crunchbaseCategoriesExclude: string[];
  // Stock listing (the ``markets`` field — actually exchange symbols,
  // not B2B/B2C). PRIVATE for unlisted, NASDAQ/NYSE/etc for listed.
  stockListings: string[];
  // Numeric headcount min/max (employee_metrics.latest_count).
  headcountLatestMin: string;
  headcountLatestMax: string;
  // Employee growth — verified all 4 windows.
  growth1mMin: string;
  growth3mMin: string;
  growth12mMin: string;
  // Follower metrics.
  followersMin: string;
  followersGrowth6mMin: string;
  followersGrowth12mMin: string;
  // Funding + IPO dates.
  lastFundingDateAfter: string;
  ipoDateAfter: string;
  lastFundingRoundAmountMin: string;
  lastFundingRoundAmountMax: string;
  // Valuation (separate from revenue).
  valuationMin?: string;
  valuationMax?: string;
  // Bootstrapped pseudo-toggle → translates to
  // ``crunchbase_total_investment_usd <= 0``.
  bootstrappedOnly: boolean;
  // Acquisition status — single canonical "acquired" toggle.
  acquiredOnly: boolean;
  // Public-company filters.
  fundingRoundTypesExclude: string[];
  investorsInclude: string[];
  investorsExclude: string[];
  stockSymbols: string[];
  // Web presence.
  domainsList: string[];
  // Bulk exact-name match (``company_name IN [...]``). The scalar
  // ``companyName`` above stays for fuzzy single-name search; this list
  // is for matching many exact names at once, populated via bulk paste.
  // Note Crustdata's ``in`` operator is exact + case-sensitive.
  companyNamesList: string[];
  /** Company LinkedIn pages (``linkedin_profile_url in``), normalized to
   *  ``https://www.linkedin.com/company/<slug>`` as the API does. */
  linkedinUrls: string[];
  twitterHandle: string;
  // Description / keyword search.
  descriptionKeyword: string;
  // Metadata.
  updatedAfter: string;
  // Composite mode — flag toggled to true when this filter blob is
  // being used as the company_filter input of /people-search/by-companies.
  // Has no effect on the company search itself.
  compositeMode: boolean;
  // Workforce composition rules. Each rule emits one CompanyDB
  // condition under role_distribution_percent.<Role>,
  // role_growth_6m.<role>, or role_growth_yoy.<role>. Multiple rules
  // are AND-combined (same as the rest of the filter blob).
  workforceRules: Array<{
    role: string;
    metric: 'percent' | 'growth_6m' | 'growth_yoy';
    op: '>=' | '<=';
    threshold: number;
  }>;
  // ── Company excludes (Find Companies only) ──
  //
  // Same names as the people side. They are not wire filters: they never reach
  // ``buildCompanyFilters`` and an exclude alone is not a search. The page sends
  // them as the request's ``exclusions`` (see ``buildCompanyExclusions``). The
  // People page's composite company slice shares this type and never sends them.
  excludeCompanyDomains: string[];
  /** Companies already in the user's lists: ``any`` list, or the named ones. */
  excludeInCleanlist: 'off' | 'any' | 'list';
  excludeInCleanlistListIds: string[];
  /** CRM exclude clauses; only the conditions that exclude accounts. */
  crmFilterSections: CrmFilterSections;
}

export const INITIAL_COMPANY_FILTERS: CompanySearchFilters = {
  companyName: '',
  companyNameMatchType: 'contains',
  domain: '',
  industriesInclude: [],
  naicsCodes: [],
  headcountRanges: [],
  revenueMin: '',
  revenueMax: '',
  yearFoundedMin: '',
  yearFoundedMax: '',
  companyType: [],
  fundingRoundTypes: [],
  fundingAmountMin: '',
  growthMin: '',
  growthMax: '',
  industriesExclude: [],
  headcountRangesExclude: [],
  companyTypeExclude: [],
  hqCountriesIso3: [],
  hqCountriesIso3Exclude: [],
  largestHeadcountCountriesIso3: [],
  hqStates: [],
  hqStatesExclude: [],
  hqCities: [],
  hqCitiesExclude: [],
  linkedinCategoriesInclude: [],
  crunchbaseCategoriesInclude: [],
  crunchbaseCategoriesExclude: [],
  stockListings: [],
  headcountLatestMin: '',
  headcountLatestMax: '',
  growth1mMin: '',
  growth3mMin: '',
  growth12mMin: '',
  followersMin: '',
  followersGrowth6mMin: '',
  followersGrowth12mMin: '',
  lastFundingDateAfter: '',
  ipoDateAfter: '',
  lastFundingRoundAmountMin: '',
  lastFundingRoundAmountMax: '',
  bootstrappedOnly: false,
  acquiredOnly: false,
  fundingRoundTypesExclude: [],
  investorsInclude: [],
  investorsExclude: [],
  stockSymbols: [],
  domainsList: [],
  companyNamesList: [],
  linkedinUrls: [],
  twitterHandle: '',
  descriptionKeyword: '',
  updatedAfter: '',
  compositeMode: false,
  workforceRules: [],
  excludeCompanyDomains: [],
  excludeInCleanlist: 'off',
  excludeInCleanlistListIds: [],
  crmFilterSections: { salesforce: [], hubspot: [] },
};

// Display labels -> API values. API expects no commas in ranges.
export const HEADCOUNT_RANGES: Array<{ label: string; value: string }> = [
  { label: '1-10', value: '1-10' },
  { label: '11-50', value: '11-50' },
  { label: '51-200', value: '51-200' },
  { label: '201-500', value: '201-500' },
  { label: '501-1,000', value: '501-1000' },
  { label: '1,001-5,000', value: '1001-5000' },
  { label: '5,001-10,000', value: '5001-10000' },
  { label: '10,001+', value: '10001+' },
];

// Headcount buckets resolve to NUMERIC bounds on the live tracked count, not to
// Crustdata's ``employee_count_range`` string.
//
// ``employee_count_range`` is LinkedIn's self-declared company-size band: set
// once, almost never corrected. Filtering it returns companies that are not
// actually that size. Measured live on "51-200" + USA: of 25 sampled results,
// 14 (56%) were outside 51-200 — including a 6-person company and a 713-person
// company. Filtering ``employee_metrics.latest_count`` on the same search
// returned 25/25 genuinely in range, and the field is populated on every row we
// sampled, so switching costs no coverage.
//
// Bonus: this also removes the ``1-10`` dead-bucket problem. Crustdata's enum has
// no ``1-10`` (it splits into ``2-10`` + ``myself only``), so the string filter
// matched ZERO rows and hid ~13M companies. Numeric bounds have no enum to miss.
const HEADCOUNT_BUCKET_BOUNDS: Record<string, [number, number | null]> = {
  '1-10': [1, 10],
  '11-50': [11, 50],
  '51-200': [51, 200],
  '201-500': [201, 500],
  '501-1000': [501, 1000],
  '1001-5000': [1001, 5000],
  '5001-10000': [5001, 10000],
  '10001+': [10001, null],
};

const HEADCOUNT_COUNT_FIELD = 'employee_metrics.latest_count';

/** ``min <= latest_count <= max`` for one bucket. */
function headcountBandConditions(bounds: [number, number | null]) {
  const [min, max] = bounds;
  const conds: FilterCondition[] = [
    { filter_type: HEADCOUNT_COUNT_FIELD, type: '=>', value: min },
  ];
  if (max !== null) {
    conds.push({ filter_type: HEADCOUNT_COUNT_FIELD, type: '=<', value: max });
  }
  return conds;
}

/**
 * Selected buckets are alternatives, so they must be OR'd — AND-ing "51-200"
 * with "201-500" matches nothing, and collapsing them to one min..max span
 * leaks every company in the gap between non-adjacent picks (verified: a
 * 1-10 + 501-1000 span returned 23, 13 and 11). Crustdata accepts nested
 * ``{op:'or'}`` groups, confirmed live.
 */
function headcountBucketFilter(values: string[]): FilterCondition | null {
  const known = values
    .map((v) => HEADCOUNT_BUCKET_BOUNDS[v])
    .filter((b): b is [number, number | null] => Boolean(b));
  if (known.length === 0) return null;
  if (known.length === 1) {
    return { op: 'and', conditions: headcountBandConditions(known[0]) };
  }
  return {
    op: 'or',
    conditions: known.map((b) => ({
      op: 'and',
      conditions: headcountBandConditions(b),
    })),
  };
}

/**
 * Exclusion is the negation of the same bands: a company is kept when it falls
 * OUTSIDE every excluded band. Expressed as an AND of per-band
 * ``(count < min OR count > max)`` groups rather than a NOT, which Crustdata
 * has no operator for.
 */
function headcountBucketExcludeFilters(values: string[]): FilterCondition[] {
  const out: FilterCondition[] = [];
  for (const v of values) {
    const bounds = HEADCOUNT_BUCKET_BOUNDS[v];
    if (!bounds) continue;
    const [min, max] = bounds;
    const alternatives: FilterCondition[] = [
      { filter_type: HEADCOUNT_COUNT_FIELD, type: '<', value: min },
    ];
    if (max !== null) {
      alternatives.push({
        filter_type: HEADCOUNT_COUNT_FIELD,
        type: '>',
        value: max,
      });
    }
    out.push({ op: 'or', conditions: alternatives });
  }
  return out;
}

export const COMPANY_TYPES = [
  'Privately Held',
  'Public Company',
  'Non Profit',
  'Government Agency',
  'Partnership',
  'Self Employed',
  'Educational Institution',
];

// Display labels for UI -> API values (canonical lowercase enum).
//
// Crustdata's verified 22-value enum on
// /screener/companydb/autocomplete?field=last_funding_round_type.
// The portal previously shipped a 14-value list including ``venture``
// and ``crowdfunding`` which aren't in the canonical enum at all
// (saved-search load alias-expands them so they still work, but new
// picks emit canonical values directly).
const FUNDING_API_VALUES_CANONICAL: Record<string, string> = {
  'Pre-Seed': 'pre_seed',
  Seed: 'seed',
  'Series A': 'series_a',
  'Series B': 'series_b',
  'Series C': 'series_c',
  'Series D': 'series_d',
  'Series E': 'series_e',
  'Series F': 'series_f',
  'Series G': 'series_g',
  'Series H': 'series_h',
  'Series Unknown': 'series_unknown',
  Angel: 'angel',
  'Convertible Note': 'convertible_note',
  'Corporate Round': 'corporate_round',
  'Debt Financing': 'debt_financing',
  'Post-IPO Debt': 'post_ipo_debt',
  'Post-IPO Equity': 'post_ipo_equity',
  'Post-IPO Secondary': 'post_ipo_secondary',
  'Private Equity': 'private_equity',
  'Secondary Market': 'secondary_market',
  'Non-Equity Assistance': 'non_equity_assistance',
  Grant: 'grant',
  Undisclosed: 'undisclosed',
};

// Legacy display labels — saved searches authored before the funding
// enum expanded (pre-2026-05) used these names. ``migrateCompanyFilters``
// rewrites them on load, but ``buildCompanyFilters`` also folds them
// in here as a defense-in-depth net so any path that bypassed the
// migrator (URL hydration, direct setFilters, raw saved-search blobs)
// still emits a valid ``last_funding_round_type`` condition instead of
// silently dropping every entry on ``.filter(Boolean)``.
const FUNDING_API_VALUES_LEGACY_ALIASES: Record<string, string> = {
  'Series F+': 'series_f',
  Venture: 'series_unknown',
  Crowdfunding: 'undisclosed',
  Debt: 'debt_financing',
};

const FUNDING_API_VALUES: Record<string, string> = {
  ...FUNDING_API_VALUES_CANONICAL,
  ...FUNDING_API_VALUES_LEGACY_ALIASES,
};

// Exported UI option list — canonical labels only. Legacy aliases stay
// out of the dropdown so users don't see deprecated picks; they're
// only used by the lookup in buildCompanyFilters above.
export const FUNDING_ROUND_TYPES = Object.keys(FUNDING_API_VALUES_CANONICAL);

/** Company keys that are settings or exclusions rather than filters: they
 *  never make a company search on their own. Everything else in
 *  ``INITIAL_COMPANY_FILTERS`` is a filter. */
export const COMPANY_NON_FILTER_KEYS: ReadonlySet<keyof CompanySearchFilters> =
  new Set<keyof CompanySearchFilters>([
    'companyNameMatchType',
    'compositeMode',
    'excludeCompanyDomains',
    'excludeInCleanlist',
    'excludeInCleanlistListIds',
    'crmFilterSections',
  ]);

/**
 * Every company filter key holding a value, read off the complete key list
 * (``INITIAL_COMPANY_FILTERS`` plus the startup program filter) rather than a
 * hand-kept one, so a key added later is counted without anyone remembering
 * to list it.
 */
export function activeCompanyFilterKeys(
  filters: CompanySearchFilters
): Array<keyof CompanySearchFilters> {
  const out: Array<keyof CompanySearchFilters> = [];
  if (hasStartupPrograms(filters.startupPrograms)) out.push('startupPrograms');
  for (const key of Object.keys(INITIAL_COMPANY_FILTERS) as Array<
    keyof CompanySearchFilters
  >) {
    if (COMPANY_NON_FILTER_KEYS.has(key)) continue;
    const value = filters[key];
    if (
      (typeof value === 'string' && value.trim() !== '') ||
      (Array.isArray(value) && value.length > 0) ||
      value === true
    )
      out.push(key);
  }
  return out;
}

export function hasAnyCompanyFilter(filters: CompanySearchFilters): boolean {
  if (hasStartupPrograms(filters.startupPrograms)) return true;
  // Group the checks so a future filter addition only needs one
  // line — and the active-filter pill strip stays in sync without
  // hunting through this OR chain.
  const stringFields: Array<keyof CompanySearchFilters> = [
    'companyName',
    'domain',
    'revenueMin',
    'revenueMax',
    'yearFoundedMin',
    'yearFoundedMax',
    'fundingAmountMin',
    'growthMin',
    'growthMax',
    'headcountLatestMin',
    'headcountLatestMax',
    'growth1mMin',
    'growth3mMin',
    'growth12mMin',
    'followersMin',
    'followersGrowth6mMin',
    'followersGrowth12mMin',
    'lastFundingDateAfter',
    'ipoDateAfter',
    'lastFundingRoundAmountMin',
    'lastFundingRoundAmountMax',
    'twitterHandle',
    'descriptionKeyword',
    'updatedAfter',
  ];
  const arrayFields: Array<keyof CompanySearchFilters> = [
    'industriesInclude',
    'naicsCodes',
    'headcountRanges',
    'companyType',
    'fundingRoundTypes',
    'industriesExclude',
    'headcountRangesExclude',
    'companyTypeExclude',
    'hqCountriesIso3',
    'hqCountriesIso3Exclude',
    'largestHeadcountCountriesIso3',
    'hqStates',
    'hqStatesExclude',
    'hqCities',
    'hqCitiesExclude',
    'linkedinCategoriesInclude',
    'crunchbaseCategoriesInclude',
    'crunchbaseCategoriesExclude',
    'stockListings',
    'fundingRoundTypesExclude',
    'investorsInclude',
    'investorsExclude',
    'stockSymbols',
    'domainsList',
    'companyNamesList',
    'linkedinUrls',
  ];
  const booleanFields: Array<keyof CompanySearchFilters> = [
    'bootstrappedOnly',
    'acquiredOnly',
  ];
  for (const f of stringFields) {
    const v = filters[f];
    if (typeof v === 'string' && v.trim() !== '') return true;
  }
  for (const f of arrayFields) {
    const v = filters[f];
    if (Array.isArray(v) && v.length > 0) return true;
  }
  for (const f of booleanFields) {
    if (filters[f] === true) return true;
  }
  if (filters.workforceRules.length > 0) return true;
  // The company excludes are deliberately not counted: excluding companies
  // narrows a search, it does not make one.
  return false;
}

/**
 * One ``hq_location`` substring test per value, OR'd together, because the
 * values in a single picker are alternatives — a company HQ is in Manchester
 * OR Nashua, never both. Returns a bare leaf for a single value so the common
 * case stays flat, and null for an empty list so the caller can skip it.
 *
 * Same nested-``{op:'or'}`` shape ``headcountBucketFilter`` already uses on
 * this endpoint.
 */
/** Six-digit codes in one ``in`` leaf, each shorter code as a
 *  ``=> low AND < high`` range, the parts OR-ed. */
export function naicsFilter(codes: string[]): FilterCondition | null {
  const cleaned = [...new Set(codes.map((c) => c.trim()).filter(Boolean))];
  const exact = cleaned.filter((code) => naicsCodeRange(code) === null);
  const parts: FilterCondition[] = [];
  if (exact.length > 0) {
    parts.push({ filter_type: 'naics_code', type: 'in', value: exact });
  }
  for (const code of cleaned) {
    const bounds = naicsCodeRange(code);
    if (!bounds) continue;
    parts.push({
      op: 'and',
      conditions: [
        { filter_type: 'naics_code', type: '=>', value: bounds[0] },
        { filter_type: 'naics_code', type: '<', value: bounds[1] },
      ],
    });
  }
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  return { op: 'or', conditions: parts };
}

function hqLocationAnyOf(values: string[]): FilterCondition | null {
  const cleaned = values.map((v) => v.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  const leaf = (value: string): FilterCondition => ({
    filter_type: 'hq_location',
    type: '(.)',
    value,
  });
  if (cleaned.length === 1) return leaf(cleaned[0]);
  return { op: 'or', conditions: cleaned.map(leaf) };
}

/** Crustdata's text operator for a company-name match mode. */
export function companyNameOperator(
  matchType: CompanySearchFilters['companyNameMatchType']
): string {
  return matchType === 'exact' ? '=' : matchType === 'similar' ? '(.)' : '[.]';
}

export function buildCompanyFilters(
  input: CompanySearchFilters
): Record<string, unknown> {
  const filters = withOrderedRanges(input, [
    'growth',
    'headcountLatest',
    'lastFundingRoundAmount',
    'revenue',
    'yearFounded',
  ]);
  const conditions: FilterCondition[] = [];
  // Name, website domain and the bulk name and domain lists name companies:
  // any match counts (OR'd below when there are several), so 4 names and 3
  // domains are 7 alternatives, never an AND that matches none (Lev).
  const identity = new Set<FilterCondition>();
  const pushIdentity = (leaf: FilterCondition) => {
    identity.add(leaf);
    conditions.push(leaf);
  };

  // Company name. Substring by default: the fuzzy operator stem-matches, so
  // "Stripe" ranked "MUKESH STRIPS LIMITED" first across 8,482 rows where
  // substring returned 332 with Stripe at the top (probed 2026-09-23).
  if (filters.companyName.trim()) {
    pushIdentity({
      filter_type: 'company_name',
      type: companyNameOperator(filters.companyNameMatchType),
      value: filters.companyName.trim(),
    });
  }

  // Domain — normalized, not just trimmed: a pasted URL never matches this
  // column. An active value that cannot yield a hostname at all (a lone
  // "https://") FAILS rather than falling back to the raw text: emitting the
  // raw text would send garbage the provider matches nothing against under a
  // predicate the user believes is filtering, and dropping it would broaden.
  if (filters.domain.trim()) {
    const domain = normalizeDomain(filters.domain);
    if (!domain) {
      throw new FilterCompileError(
        'domain',
        'Company domain',
        `"${filters.domain.trim()}" is not a valid domain.`
      );
    }
    // Exact: ``(.)`` is Crustdata's fuzzy operator, and "stripe.com" under it
    // returned 892 companies (solara.co.in, strides.com…) with Stripe 8th.
    pushIdentity({
      filter_type: 'company_website_domain',
      type: '=',
      value: domain,
    });
  }

  // Industries — group tokens (e.g. "Software & IT") from the grouped
  // picker expand to their member LinkedIn industries before sending.
  // Crustdata's linkedin_industries field only matches canonical names.
  const industriesIn = expandIndustrySelection(filters.industriesInclude);
  if (industriesIn.length > 0) {
    conditions.push({
      filter_type: 'linkedin_industries',
      type: 'in',
      value: industriesIn,
    });
  }

  // NAICS: the provider's primary six-digit code; shorter codes are prefixes.
  // Blobs saved before NAICS existed have no key.
  const naics = naicsFilter(filters.naicsCodes ?? []);
  if (naics) conditions.push(naics);

  // Headcount: buckets + numeric range coexist in state (RangeField
  // exposes both), but at search time numeric range takes precedence.
  // If the user typed a custom min/max we drop the bucket array — the
  // two would otherwise AND together and produce empty results when
  // a typed range falls between bucket edges (e.g. min=75 vs the
  // 51-200 bucket). Either-or wins; numeric is the more specific intent.
  const hasNumericHeadcount =
    filters.headcountLatestMin.trim() !== '' ||
    filters.headcountLatestMax.trim() !== '';
  if (filters.headcountRanges.length > 0 && !hasNumericHeadcount) {
    const bucketFilter = headcountBucketFilter(filters.headcountRanges);
    if (bucketFilter) conditions.push(bucketFilter);
  }

  // (Removed 2026-05-24: hqLocations free-text filter. The sidebar
  //  ships hqCountriesIso3 / hqStates / hqCities scoped autocompletes
  //  exclusively now.)

  // HQ state + city scoped autocompletes. Crustdata's CompanyDB doesn't
  // expose dedicated state/city fields, so we route them through the
  // same ``hq_location`` substring filter as ``hqLocations``. The user
  // sees three scoped autocompletes in the UI (country / state / city)
  // but the BE just sees a list of substring contains/not_contains
  // conditions over the address blob.
  //
  // Precedence: city > state > country includes. When the user picks a
  // city, the broader country filter ANDs unnecessarily against the
  // same address blob and produces zero results (e.g. picking "Toronto"
  // + "North America" group → "Toronto" passes the state/city test but
  // the country filter expands to USA/CAN/MEX as a single OR'd
  // substring test; both must hit which under-matches in practice).
  // Most-specific include wins. Excludes pass through verbatim.
  const hqStateInclude = filters.hqStates ?? [];
  const hqStateExclude = filters.hqStatesExclude ?? [];
  const hqCityInclude = filters.hqCities ?? [];
  const hqCityExclude = filters.hqCitiesExclude ?? [];
  // Multiple cities (or multiple states) are ALTERNATIVES, not additional
  // constraints. Pushing each one as its own top-level condition put them
  // under the outer ``op: 'and'``, so two cities compiled to
  // ``hq_location ~ "Manchester" AND hq_location ~ "Nashua"`` — an
  // intersection no single address blob can satisfy, which returned zero
  // rows for every multi-city search. Each list gets ONE nested OR group
  // instead; the two groups still AND against each other, so a state plus a
  // city keeps narrowing as before.
  const hqCityGroup = hqLocationAnyOf(hqCityInclude);
  if (hqCityGroup) conditions.push(hqCityGroup);
  const hqStateGroup = hqLocationAnyOf(hqStateInclude);
  if (hqStateGroup) conditions.push(hqStateGroup);
  // Excludes stay one AND-ed condition each: "not Boston" AND "not Nashua"
  // is the correct reading of two exclusions, and OR-ing them would let a
  // row through as long as it failed only one.
  for (const v of [...hqStateExclude, ...hqCityExclude]) {
    conditions.push({
      filter_type: 'hq_location',
      type: 'not_contains',
      value: v,
    });
  }

  // Year founded range
  if (filters.yearFoundedMin) {
    const val = Number(filters.yearFoundedMin);
    if (!Number.isNaN(val)) {
      conditions.push({
        filter_type: 'year_founded',
        type: '=>',
        value: val,
      });
    }
  }
  if (filters.yearFoundedMax) {
    const val = Number(filters.yearFoundedMax);
    if (!Number.isNaN(val)) {
      conditions.push({
        filter_type: 'year_founded',
        type: '=<',
        value: val,
      });
    }
  }

  // Company type
  if (filters.companyType.length > 0) {
    conditions.push({
      filter_type: 'company_type',
      type: 'in',
      value: filters.companyType,
    });
  }

  // Funding round types - map display labels to API values
  if (filters.fundingRoundTypes.length > 0) {
    const apiValues = filters.fundingRoundTypes
      .map((label) => FUNDING_API_VALUES[label])
      .filter(Boolean);
    if (apiValues.length > 0) {
      conditions.push({
        filter_type: 'last_funding_round_type',
        type: 'in',
        value: apiValues,
      });
    }
  }

  // Funding amount minimum
  if (filters.fundingAmountMin) {
    const val = Number(filters.fundingAmountMin);
    if (!Number.isNaN(val)) {
      conditions.push({
        filter_type: 'crunchbase_total_investment_usd',
        type: '=>',
        value: val,
      });
    }
  }

  // Employee growth (6-month percent)
  if (filters.growthMin) {
    const val = Number(filters.growthMin);
    if (!Number.isNaN(val)) {
      conditions.push({
        filter_type: 'employee_metrics.growth_6m_percent',
        type: '=>',
        value: val,
      });
    }
  }
  if (filters.growthMax) {
    const val = Number(filters.growthMax);
    if (!Number.isNaN(val)) {
      conditions.push({
        filter_type: 'employee_metrics.growth_6m_percent',
        type: '<',
        value: val,
      });
    }
  }

  // ── Expanded filters (PRD 2026-05) ──
  //
  // Each block is independent — only emits a condition when the field
  // is set. Order doesn't matter for an AND group.

  const industriesEx = expandIndustrySelection(filters.industriesExclude);
  if (industriesEx.length > 0) {
    conditions.push({
      filter_type: 'linkedin_industries',
      type: 'not_in',
      value: industriesEx,
    });
  }
  if (filters.headcountRangesExclude.length > 0) {
    conditions.push(
      ...headcountBucketExcludeFilters(filters.headcountRangesExclude)
    );
  }
  if (filters.companyTypeExclude.length > 0) {
    conditions.push({
      filter_type: 'company_type',
      type: 'not_in',
      value: filters.companyTypeExclude,
    });
  }
  // HQ country / largest-headcount-country arrays may contain group
  // tokens ("EMEA", "APAC") emitted by the grouped FilterPicker.
  // Expand them to underlying ISO-3 codes before sending to Crustdata
  // — the BE only matches the canonical iso3 set, not group names.
  // HQ country precedence — when the user has picked a state or city
  // (also routed through the hq_location substring filter), skip the
  // hq_country include filter. Otherwise the AND between
  // hq_country IN [USA, CAN, MEX] AND hq_location ~ "Toronto" rarely
  // matches the same row because Crustdata stores hq_country and
  // hq_location independently. Most-specific include wins.
  const hasHqCity = (filters.hqCities ?? []).length > 0;
  const hasHqState = (filters.hqStates ?? []).length > 0;
  const skipCountryInclude = hasHqCity || hasHqState;
  const hqInclude = skipCountryInclude
    ? []
    : expandCountrySelection(filters.hqCountriesIso3);
  const hqExclude = expandCountrySelection(filters.hqCountriesIso3Exclude);
  const largestInclude = expandCountrySelection(
    filters.largestHeadcountCountriesIso3
  );
  if (hqInclude.length > 0) {
    conditions.push({
      filter_type: 'hq_country',
      type: 'in',
      value: hqInclude,
    });
  }
  if (hqExclude.length > 0) {
    conditions.push({
      filter_type: 'hq_country',
      type: 'not_in',
      value: hqExclude,
    });
  }
  if (largestInclude.length > 0) {
    conditions.push({
      filter_type: 'largest_headcount_country',
      type: 'in',
      value: largestInclude,
    });
  }
  if (filters.linkedinCategoriesInclude.length > 0) {
    conditions.push({
      filter_type: 'linkedin_categories',
      type: 'in',
      value: filters.linkedinCategoriesInclude,
    });
  }
  if (filters.crunchbaseCategoriesInclude.length > 0) {
    conditions.push({
      filter_type: 'crunchbase_categories',
      type: 'in',
      value: filters.crunchbaseCategoriesInclude,
    });
  }
  if (filters.crunchbaseCategoriesExclude.length > 0) {
    conditions.push({
      filter_type: 'crunchbase_categories',
      type: 'not_in',
      value: filters.crunchbaseCategoriesExclude,
    });
  }
  if (filters.stockListings.length > 0) {
    conditions.push({
      filter_type: 'markets',
      type: 'in',
      value: filters.stockListings,
    });
  }

  // Numeric headcount (latest count, not bucket range).
  const headcountMin = Number(filters.headcountLatestMin);
  if (filters.headcountLatestMin && !Number.isNaN(headcountMin)) {
    conditions.push({
      filter_type: 'employee_metrics.latest_count',
      type: '=>',
      value: headcountMin,
    });
  }
  const headcountMax = Number(filters.headcountLatestMax);
  if (filters.headcountLatestMax && !Number.isNaN(headcountMax)) {
    conditions.push({
      filter_type: 'employee_metrics.latest_count',
      type: '=<',
      value: headcountMax,
    });
  }

  // Employee growth — additional windows.
  const growth1m = Number(filters.growth1mMin);
  if (filters.growth1mMin && !Number.isNaN(growth1m)) {
    conditions.push({
      filter_type: 'employee_metrics.growth_1m_percent',
      type: '=>',
      value: growth1m,
    });
  }
  const growth3m = Number(filters.growth3mMin);
  if (filters.growth3mMin && !Number.isNaN(growth3m)) {
    conditions.push({
      filter_type: 'employee_metrics.growth_3m_percent',
      type: '=>',
      value: growth3m,
    });
  }
  const growth12m = Number(filters.growth12mMin);
  if (filters.growth12mMin && !Number.isNaN(growth12m)) {
    conditions.push({
      filter_type: 'employee_metrics.growth_12m_percent',
      type: '=>',
      value: growth12m,
    });
  }

  // Follower metrics.
  const followersMin = Number(filters.followersMin);
  if (filters.followersMin && !Number.isNaN(followersMin)) {
    conditions.push({
      filter_type: 'follower_metrics.latest_count',
      type: '=>',
      value: followersMin,
    });
  }
  const followers6m = Number(filters.followersGrowth6mMin);
  if (filters.followersGrowth6mMin && !Number.isNaN(followers6m)) {
    conditions.push({
      filter_type: 'follower_metrics.growth_6m_percent',
      type: '=>',
      value: followers6m,
    });
  }
  const followers12m = Number(filters.followersGrowth12mMin);
  if (filters.followersGrowth12mMin && !Number.isNaN(followers12m)) {
    conditions.push({
      filter_type: 'follower_metrics.growth_12m_percent',
      type: '=>',
      value: followers12m,
    });
  }

  // Funding + IPO dates.
  if (filters.lastFundingDateAfter) {
    conditions.push({
      filter_type: 'last_funding_date',
      type: '=>',
      value: normalizeCalendarDate(
        filters.lastFundingDateAfter,
        'lastFundingDateAfter',
        'Last funding after'
      ),
    });
  }
  if (filters.ipoDateAfter) {
    conditions.push({
      filter_type: 'ipo_date',
      type: '=>',
      value: normalizeCalendarDate(
        filters.ipoDateAfter,
        'ipoDateAfter',
        'IPO date after'
      ),
    });
  }
  const fundingRoundMin = Number(filters.lastFundingRoundAmountMin);
  if (filters.lastFundingRoundAmountMin && !Number.isNaN(fundingRoundMin)) {
    conditions.push({
      filter_type: 'last_funding_round_investment_usd',
      type: '=>',
      value: fundingRoundMin,
    });
  }
  const fundingRoundMax = Number(filters.lastFundingRoundAmountMax);
  if (filters.lastFundingRoundAmountMax && !Number.isNaN(fundingRoundMax)) {
    conditions.push({
      filter_type: 'last_funding_round_investment_usd',
      type: '=<',
      value: fundingRoundMax,
    });
  }

  // Annual revenue. Crustdata stores revenue as an estimated BAND
  // (estimated_revenue_lower_bound_usd .. estimated_revenue_higher_bound_usd),
  // never a point value, so a min/max pair maps to the two bound columns —
  // not to one column twice. Containment semantics: the company's whole
  // estimated band must sit inside the requested window, which is what
  // "revenue between X and Y" means to a prospector and is what Crustdata's
  // own documented example does. Note the spelling is ``higher``, not
  // ``upper`` — ``upper`` belongs to the /screener/company identify endpoint.
  //
  // Until this block existed the Revenue min/max was collected in the sidebar,
  // rendered as an active filter pill, and then silently dropped from the
  // query, so every "revenue >= $10M" search returned unfiltered results.
  const revMin = Number(filters.revenueMin);
  if (filters.revenueMin && !Number.isNaN(revMin)) {
    conditions.push({
      filter_type: 'estimated_revenue_lower_bound_usd',
      type: '=>',
      value: revMin,
    });
  }
  const revMax = Number(filters.revenueMax);
  if (filters.revenueMax && !Number.isNaN(revMax)) {
    conditions.push({
      filter_type: 'estimated_revenue_higher_bound_usd',
      type: '=<',
      value: revMax,
    });
    // Crustdata fills 0 — not null — when it has no revenue estimate (~20% of
    // rows in a live sample). Without this guard a max-only filter matches
    // every company with NO revenue data at all, which is worse than not
    // filtering. Pairing the bound with ``> 0`` is Crustdata's documented way
    // to express "this numeric field is actually populated".
    conditions.push({
      filter_type: 'estimated_revenue_higher_bound_usd',
      type: '>',
      value: 0,
    });
  }

  // Bootstrapped: total investment <= 0.
  if (filters.bootstrappedOnly) {
    conditions.push({
      filter_type: 'crunchbase_total_investment_usd',
      type: '=<',
      value: 0,
    });
  }

  // Acquired only — single canonical wire value per probe.
  if (filters.acquiredOnly) {
    conditions.push({
      filter_type: 'acquisition_status',
      type: '=',
      value: 'acquired',
    });
  }

  // Funding round excludes (e.g. "not seed / not series_a").
  if (filters.fundingRoundTypesExclude.length > 0) {
    const apiValues = filters.fundingRoundTypesExclude
      .map((label) => FUNDING_API_VALUES[label])
      .filter(Boolean);
    if (apiValues.length > 0) {
      conditions.push({
        filter_type: 'last_funding_round_type',
        type: 'not_in',
        value: apiValues,
      });
    }
  }

  // Investors.
  if (filters.investorsInclude.length > 0) {
    conditions.push({
      filter_type: 'crunchbase_investors',
      type: 'in',
      value: filters.investorsInclude,
    });
  }
  if (filters.investorsExclude.length > 0) {
    conditions.push({
      filter_type: 'crunchbase_investors',
      type: 'not_in',
      value: filters.investorsExclude,
    });
  }
  if (filters.stockSymbols.length > 0) {
    conditions.push({
      filter_type: 'stock_symbols',
      type: 'in',
      value: filters.stockSymbols,
    });
  }

  // Web presence.
  const normalizedDomainsList = normalizeDomainList(filters.domainsList);
  if (filters.domainsList.length > 0 && normalizedDomainsList.length === 0) {
    // Same contract as the single ``domain`` field above: an active control
    // whose every entry is unusable must fail closed, not silently drop the
    // predicate and broaden the search.
    throw new FilterCompileError(
      'domainsList',
      'Company domains',
      'none of the bulk domains are usable. Remove or fix the entries.'
    );
  }
  if (normalizedDomainsList.length > 0) {
    pushIdentity({
      filter_type: 'domains',
      type: 'in',
      value: normalizedDomainsList,
    });
  }
  // Bulk exact company names — same ``in`` shape as domains above.
  if (filters.companyNamesList.length > 0) {
    pushIdentity({
      filter_type: 'company_name',
      type: 'in',
      value: filters.companyNamesList,
    });
  }
  // Company LinkedIn pages, exact. Same fail-closed rule as the domains.
  const linkedinUrls = normalizeCompanyLinkedInUrls(filters.linkedinUrls ?? []);
  if ((filters.linkedinUrls ?? []).length > 0 && linkedinUrls.length === 0) {
    throw new FilterCompileError(
      'linkedinUrls',
      'Company LinkedIn URLs',
      'none of the LinkedIn URLs is a company page (linkedin.com/company/...). Remove or fix the entries.'
    );
  }
  if (linkedinUrls.length > 0) {
    pushIdentity({
      filter_type: 'linkedin_profile_url',
      type: 'in',
      value: linkedinUrls,
    });
  }
  if (filters.twitterHandle.trim()) {
    conditions.push({
      filter_type: 'twitter_handle',
      type: '=',
      value: filters.twitterHandle.trim(),
    });
  }

  // Description / keyword search.
  if (filters.descriptionKeyword.trim()) {
    conditions.push({
      filter_type: 'linkedin_company_description',
      type: '(.)',
      value: filters.descriptionKeyword.trim(),
    });
  }

  // Metadata.
  if (filters.updatedAfter) {
    conditions.push({
      filter_type: 'updated_at',
      type: '=>',
      value: filters.updatedAfter,
    });
  }

  // Workforce composition rules. Each rule maps to a single CompanyDB
  // condition on a nested role path. Crustdata uses ``=>`` / ``=<``
  // for ≥ / ≤ comparisons (note the unusual order — see the rest of
  // this file for matching usage).
  for (const rule of filters.workforceRules) {
    const path =
      rule.metric === 'percent'
        ? `role_distribution_percent.${rule.role}`
        : rule.metric === 'growth_6m'
          ? `role_growth_6m.${rule.role}`
          : `role_growth_yoy.${rule.role}`;
    conditions.push({
      filter_type: path,
      type: rule.op === '>=' ? '=>' : '=<',
      value: rule.threshold,
    });
  }

  // Several company identities become one OR group where the first one sat;
  // a single one stays exactly as it was sent before.
  if (identity.size > 1) {
    const at = conditions.findIndex((c) => identity.has(c));
    const rest = conditions.filter((c) => !identity.has(c));
    rest.splice(at, 0, { op: 'or', conditions: [...identity] });
    conditions.length = 0;
    conditions.push(...rest);
  }

  if (conditions.length === 0) return {};

  return {
    op: 'and',
    conditions,
  };
}

export interface ActiveCompanyFilterOptions {
  /** Chips for the company excludes. Off while the exclusions flag is off, so
   *  stale state never shows a filter the page will not send. Default on. */
  exclusions?: boolean;
  /** Readable CRM clause labels (owner names, field labels). */
  crmChipCtx?: CrmFilterChipContext;
}

export function getActiveCompanyFilters(
  filters: CompanySearchFilters,
  options: ActiveCompanyFilterOptions = {}
): ActiveFilter[] {
  const active: ActiveFilter[] = [];

  if (hasStartupPrograms(filters.startupPrograms)) {
    active.push({
      key: 'startupPrograms',
      label: 'Startup programs',
      value: 'selected',
      displayValue: `Startup programs (${filters.startupPrograms?.selections?.length ?? 0})`,
    });
  }
  if (filters.companyName.trim()) {
    active.push({
      key: 'companyName',
      label: 'Company',
      value: filters.companyName,
    });
  }
  if (filters.domain.trim()) {
    active.push({ key: 'domain', label: 'Domain', value: filters.domain });
  }
  for (const v of filters.industriesInclude) {
    active.push({ key: 'industriesInclude', label: 'Industry', value: v });
  }
  for (const v of filters.naicsCodes ?? []) {
    active.push({
      key: 'naicsCodes',
      label: 'NAICS',
      value: v,
      displayValue: naicsLabel(v),
    });
  }
  for (const v of filters.headcountRanges) {
    const range = HEADCOUNT_RANGES.find((r) => r.value === v);
    // Use stored value for removal matching; display label in the label field
    active.push({
      key: 'headcountRanges',
      label: `Headcount: ${range?.label ?? v}`,
      value: v,
    });
  }
  if (filters.revenueMin) {
    active.push({
      key: 'revenueMin',
      label: 'Revenue Min',
      value: `$${Number(filters.revenueMin).toLocaleString()}`,
    });
  }
  if (filters.revenueMax) {
    active.push({
      key: 'revenueMax',
      label: 'Revenue Max',
      value: `$${Number(filters.revenueMax).toLocaleString()}`,
    });
  }
  if (filters.yearFoundedMin) {
    active.push({
      key: 'yearFoundedMin',
      label: 'Founded After',
      value: filters.yearFoundedMin,
    });
  }
  if (filters.yearFoundedMax) {
    active.push({
      key: 'yearFoundedMax',
      label: 'Founded Before',
      value: filters.yearFoundedMax,
    });
  }
  for (const v of filters.companyType) {
    active.push({ key: 'companyType', label: 'Type', value: v });
  }
  for (const v of filters.fundingRoundTypes) {
    active.push({ key: 'fundingRoundTypes', label: 'Funding', value: v });
  }
  if (filters.fundingAmountMin) {
    active.push({
      key: 'fundingAmountMin',
      label: 'Min Funding',
      value: `$${Number(filters.fundingAmountMin).toLocaleString()}`,
    });
  }
  if (filters.growthMin) {
    active.push({
      key: 'growthMin',
      label: 'Growth Min',
      value: `${filters.growthMin}%`,
    });
  }
  if (filters.growthMax) {
    active.push({
      key: 'growthMax',
      label: 'Growth Max',
      value: `${filters.growthMax}%`,
    });
  }

  // ── Expanded filters (PRD 2026-05) ──
  for (const v of filters.industriesExclude) {
    active.push({
      key: 'industriesExclude',
      label: 'Industry',
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.headcountRangesExclude) {
    const range = HEADCOUNT_RANGES.find((r) => r.value === v);
    active.push({
      key: 'headcountRangesExclude',
      label: `Headcount: ${range?.label ?? v}`,
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.companyTypeExclude) {
    active.push({
      key: 'companyTypeExclude',
      label: 'Type',
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.hqCountriesIso3) {
    active.push({ key: 'hqCountriesIso3', label: 'HQ Country', value: v });
  }
  for (const v of filters.hqCountriesIso3Exclude) {
    active.push({
      key: 'hqCountriesIso3Exclude',
      label: 'HQ Country',
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.largestHeadcountCountriesIso3) {
    active.push({
      key: 'largestHeadcountCountriesIso3',
      label: 'Most-In',
      value: v,
    });
  }
  for (const v of filters.hqStates ?? []) {
    active.push({ key: 'hqStates', label: 'HQ State', value: v });
  }
  for (const v of filters.hqStatesExclude ?? []) {
    active.push({
      key: 'hqStatesExclude',
      label: 'HQ State',
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.hqCities ?? []) {
    active.push({ key: 'hqCities', label: 'HQ City', value: v });
  }
  for (const v of filters.hqCitiesExclude ?? []) {
    active.push({
      key: 'hqCitiesExclude',
      label: 'HQ City',
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.linkedinCategoriesInclude) {
    active.push({
      key: 'linkedinCategoriesInclude',
      label: 'Category',
      value: v,
    });
  }
  for (const v of filters.crunchbaseCategoriesInclude) {
    active.push({
      key: 'crunchbaseCategoriesInclude',
      label: 'Crunchbase Tag',
      value: v,
    });
  }
  for (const v of filters.crunchbaseCategoriesExclude) {
    active.push({
      key: 'crunchbaseCategoriesExclude',
      label: 'Crunchbase Tag',
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.stockListings) {
    active.push({ key: 'stockListings', label: 'Listing', value: v });
  }
  if (filters.headcountLatestMin) {
    active.push({
      key: 'headcountLatestMin',
      label: 'Headcount Min',
      value: filters.headcountLatestMin,
    });
  }
  if (filters.headcountLatestMax) {
    active.push({
      key: 'headcountLatestMax',
      label: 'Headcount Max',
      value: filters.headcountLatestMax,
    });
  }
  if (filters.growth1mMin) {
    active.push({
      key: 'growth1mMin',
      label: '1m Growth',
      value: `${filters.growth1mMin}%+`,
    });
  }
  if (filters.growth3mMin) {
    active.push({
      key: 'growth3mMin',
      label: '3m Growth',
      value: `${filters.growth3mMin}%+`,
    });
  }
  if (filters.growth12mMin) {
    active.push({
      key: 'growth12mMin',
      label: '12m Growth',
      value: `${filters.growth12mMin}%+`,
    });
  }
  if (filters.followersMin) {
    active.push({
      key: 'followersMin',
      label: 'Followers',
      value: `${Number(filters.followersMin).toLocaleString()}+`,
    });
  }
  if (filters.followersGrowth6mMin) {
    active.push({
      key: 'followersGrowth6mMin',
      label: 'Followers 6m Growth',
      value: `${filters.followersGrowth6mMin}%+`,
    });
  }
  if (filters.followersGrowth12mMin) {
    active.push({
      key: 'followersGrowth12mMin',
      label: 'Followers 12m Growth',
      value: `${filters.followersGrowth12mMin}%+`,
    });
  }
  if (filters.lastFundingDateAfter) {
    active.push({
      key: 'lastFundingDateAfter',
      label: 'Funded After',
      value: filters.lastFundingDateAfter,
    });
  }
  if (filters.ipoDateAfter) {
    active.push({
      key: 'ipoDateAfter',
      label: 'IPO After',
      value: filters.ipoDateAfter,
    });
  }
  if (filters.bootstrappedOnly) {
    active.push({
      key: 'bootstrappedOnly',
      label: 'Bootstrapped',
      value: 'Only',
    });
  }
  if (filters.acquiredOnly) {
    active.push({ key: 'acquiredOnly', label: 'Acquired', value: 'Only' });
  }
  for (const v of filters.investorsInclude) {
    active.push({ key: 'investorsInclude', label: 'Investor', value: v });
  }
  for (const v of filters.investorsExclude) {
    active.push({
      key: 'investorsExclude',
      label: 'Investor',
      value: v,
      variant: 'exclude',
    });
  }
  for (const v of filters.stockSymbols) {
    active.push({ key: 'stockSymbols', label: 'Ticker', value: v });
  }
  for (const v of filters.fundingRoundTypesExclude) {
    active.push({
      key: 'fundingRoundTypesExclude',
      label: 'Funding',
      value: v,
      variant: 'exclude',
    });
  }
  if (filters.lastFundingRoundAmountMin) {
    active.push({
      key: 'lastFundingRoundAmountMin',
      label: 'Last Round ≥',
      value: `$${Number(filters.lastFundingRoundAmountMin).toLocaleString()}`,
    });
  }
  if (filters.lastFundingRoundAmountMax) {
    active.push({
      key: 'lastFundingRoundAmountMax',
      label: 'Last Round ≤',
      value: `$${Number(filters.lastFundingRoundAmountMax).toLocaleString()}`,
    });
  }
  for (const v of filters.domainsList) {
    active.push({ key: 'domainsList', label: 'Domain', value: v });
  }
  for (const v of filters.companyNamesList) {
    active.push({ key: 'companyNamesList', label: 'Company', value: v });
  }
  for (const v of filters.linkedinUrls ?? []) {
    active.push({ key: 'linkedinUrls', label: 'Company LinkedIn', value: v });
  }
  if (filters.twitterHandle.trim()) {
    active.push({
      key: 'twitterHandle',
      label: 'Twitter',
      value: filters.twitterHandle,
    });
  }
  if (filters.descriptionKeyword.trim()) {
    active.push({
      key: 'descriptionKeyword',
      label: 'Description',
      value: filters.descriptionKeyword,
    });
  }
  if (filters.updatedAfter) {
    active.push({
      key: 'updatedAfter',
      label: 'Updated After',
      value: filters.updatedAfter,
    });
  }
  for (const rule of filters.workforceRules) {
    const metricLabel =
      rule.metric === 'percent'
        ? '% of HC'
        : rule.metric === 'growth_6m'
          ? '6m growth'
          : 'YoY';
    active.push({
      key: 'workforceRules',
      label: `${rule.role} ${metricLabel}`,
      // ``value`` must round-trip with the rule object so removeFilter can
      // match it (the chip strip compares this against the stored rules).
      // ``displayValue`` keeps the human-friendly ">= 20%" on the pill.
      value: `${rule.role}|${rule.metric}|${rule.op}|${rule.threshold}`,
      displayValue: `${rule.op} ${rule.threshold}%`,
    });
  }

  if (options.exclusions ?? true) {
    for (const v of filters.excludeCompanyDomains ?? []) {
      active.push({
        key: 'excludeCompanyDomains',
        label: 'Exclude Domain',
        value: v,
        variant: 'exclude',
      });
    }
    const cleanlistScope = filters.excludeInCleanlist ?? 'off';
    if (cleanlistScope !== 'off') {
      const listCount = (filters.excludeInCleanlistListIds ?? []).length;
      active.push({
        key: 'excludeInCleanlist',
        label: `Already in ${BRAND.name}`,
        value:
          cleanlistScope === 'any'
            ? 'Anywhere'
            : listCount === 1
              ? '1 list'
              : `${listCount} lists`,
        variant: 'exclude',
      });
    }
    active.push(...buildCrmFilterChips(filters, options.crmChipCtx));
  }

  return active;
}

export function formatRevenue(lower?: number, upper?: number): string {
  return revenueRange(lower, upper) || '-';
}
